<template>
    <div class="product-content">
        <el-affix :z-index="maxZIndex">
            <NavBar></NavBar>
        </el-affix>

        <div class="main">
            <Breadcrumb :bread-list="breadList"></Breadcrumb>

            <div class="product-box">
                <div class="left">
                    <ProductTypeSide ref="productTypeSideRef" @select-product-type="selectProductTypeHandler"
                        :is-select="isSelectProductType">
                    </ProductTypeSide>

                    <HotSalesSide></HotSalesSide>
                </div>

                <div class="right">
                    <el-row :gutter="40" :justify="product.length > 0 ? 'start' : 'center'">
                        <el-col :span="8" v-if="product.length > 0" v-for="(item, index) in product" :key="item.id"
                            :lg="8" :md="12" :sm="24" :xs="24">
                            <div class="col">
                                <div class="img">
                                    <img :src="`${item.cover}`" fit="cover" />
                                </div>

                                <div class="name">
                                    {{ item.name }}
                                </div>

                                <div class="look-more-button">
                                    <el-button size="large" @click="gotoProductDetails(item.id)">{{
                                        $t('products.lookMoreText') }}</el-button>
                                </div>
                            </div>
                        </el-col>

                        <el-empty :image-size="200" v-else :description="$t('products.noProduct')" />

                    </el-row>
                </div>
            </div>

            <Pagination v-model:current-page="currentPage" :page-size="pageSize" :total-page="totalPage"
                @current-page-change="onPageChange" class="page"></Pagination>
        </div>

        <Footer></Footer>
    </div>
</template>

<script lang="ts" setup>
import NavBar from '@/components/NavBar.vue';
import Footer from '@/components/Footer.vue';
import { UtilMethods } from '@/util/UtilMethods';
import Breadcrumb from '@/components/Breadcrumb.vue';
import { BreadType, Product } from '@/typeDefine/EntityType';
import ProductTypeSide from '@/components/ProductTypeSide.vue';
import HotSalesSide from '@/components/HotSalesSide.vue';
import { Action } from '@/typeDefine/CommonTypes';
import { BaseResponse, GetProductByTypeResponse, SearchProductByNameResponse } from '@/typeDefine/HttpResponseType';
import { Api } from '@/http/Api';
import { ResponseCode } from '@/http/ResponseCode';
import { onMounted, ref, Ref, watch } from 'vue';
import Pagination from '@/components/Pagination.vue';
import { Loading } from '@/util/Loading';
import { ConstantField } from '@/constant/ConstantField';
import { router } from '@/router';
import { useRoute } from 'vue-router';
import { GetProductByTypeRequest, SearchProductByNameRequest } from '@/typeDefine/HttpRequestType';

type SearchNameApiFunction = (param: SearchProductByNameRequest) => Promise<SearchProductByNameResponse>
type ByTypeApiFunction = (param: GetProductByTypeRequest) => Promise<GetProductByTypeResponse>

const maxZIndex: string = UtilMethods.getDOMStyleFromCSS('--max-z-index')

const productTypeSideRef: Ref<InstanceType<typeof ProductTypeSide> | null> = ref(null)

const breadList: BreadType[] = [
    {
        text: 'index.navBar.product'
    }
]

const currentPage: Ref<number> = ref(1)
//页大小
const pageSize: Ref<number> = ref(10)
const totalPage: Ref<number> = ref(0)

//产品类型侧边栏是否默认选中
const isSelectProductType: Ref<boolean> = ref(true)

//当前选择的产品类别
const currentTypeId: Ref<string> = ref('')

//产品列表
const product: Ref<GetProductByTypeResponse['data']['product']> = ref([])

const route = useRoute()

onMounted(() => {
    isSelectProductType.value = typeof route.query.keyword === 'undefined'

    if (!isSelectProductType.value) {
        //用户搜索了产品
        requestProductBySearchName(route.query.keyword as string, currentPage.value)
    }
})

const request: Action<[SearchNameApiFunction | ByTypeApiFunction, any]> = async (api, param) => {
    Loading.instance.open('请稍后...')

    const response = await api(param)

    Loading.instance.close()

    UtilMethods.showMessageToast(response, false)

    if (response.code === ResponseCode.OK) {
        product.value = response.data.product
        totalPage.value = response.data.totalPage
    }
}

const requestProductBySearchName: Action<[string, number]> = async (searchName: string, startPage: number) => {

    await request(Api.instance.searchProductByName, {
        productName: searchName,
        startPage: startPage
    })
}

const requestProductByType: Action<[string, number]> = async (typeId: string, startPage: number) => {

    await request(Api.instance.getProductByType, {
        typeId,
        startPage: startPage
    })
}

//处理切换产品类型事件
const selectProductTypeHandler: Action<[string]> = async (typeId: string) => {

    currentTypeId.value = typeId
    requestProductByType(typeId, currentPage.value)

    if (!isSelectProductType.value) {
        router.replace({
            name: router.currentRoute.value.name
        })
    }
}

const onPageChange: Action<[number]> = (value: number) => {
    requestProductByType(currentTypeId.value, currentPage.value)
}

const gotoProductDetails: Action<[number]> = (id: number) => {
    router.push({
        name: 'productDetails',
        params: {
            id
        }
    })
}

//监听页面参数变化
watch(() => route.query.typeId,
    (value) => {
        isSelectProductType.value = true
        productTypeSideRef.value?.requestTypes()
    }, { deep: true })

watch(() => route.query.keyword,
    (value) => {
        if (value) {
            isSelectProductType.value = false
            currentPage.value = 1
            requestProductBySearchName(value as string, currentPage.value)
        }
    }, { deep: true })
</script>

<style lang="css" scoped>
.product-content .main {
    width: 90%;
    margin: 50px auto;
    min-height: calc(100vh - 70px - 300px);
    box-sizing: border-box;
}

.product-content .product-box {
    display: flex;
}

.product-content .product-box .left {
    width: 20%;
    margin-right: 50px;
}

.product-content .product-box .right {
    flex: 1;
}

.product-content .product-box .right .col {
    margin-bottom: 20px;
    border: 1px solid #EEEEEE;
    padding: 20px 0;
    transition: all ease 0.5s;
}

.product-content .product-box .right .col:hover {
    border: 1px solid var(--primary-color);
}

.product-content .product-box .right .col .img {
    overflow: hidden;
    margin-bottom: 20px;
    text-align: center;
    width: 80%;
    height: 250px;
    margin: 0 auto;
}

.product-content .product-box .right .col .img img {
    display: inline-block;
    overflow: hidden;
    transition: all ease 0.5s;
    height: 100%;
    width: 100%;
}

.product-content .product-box .right .col:hover img {
    transform: scale(1.1) !important;
}

.product-content .product-box .right .col .name {
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin: 20px 0;
}

.product-content .product-box .right .col .look-more-button {
    text-align: center;
}

.product-content .main .page {
    justify-content: end;
}

@media (max-width: 1200px) {
    .product-content .product-box .left {
        width: 15%;
    }
}
</style>